/** The factor library — **gate 20 (S12)**. Read-only, and the absences are the point.
 *
 * Every figure's receipt cites a factor; this is where those factors are listed, with the
 * publisher, edition, retrieval date and the publisher's own id for each. It reads the **database**
 * rows (KD, 2026-10-23), which are what a receipt cites; `test_factor_library_agrees_with_engine`
 * is what keeps them from drifting from the sets the engine calculates with.
 *
 * ## What this screen does not have, and a test asserts each
 *
 * - **No control that edits.** A factor set is locked when it is loaded (ADR-0012) and a
 *   publisher's correction is a new edition, so there is nothing here to change. The only button on
 *   the screen is *Try again*, and only when a read failed. The three controls it has - a search, a
 *   kind-of-factor select and a CO2-only switch - narrow rows already held and send nothing.
 * - **No edition selector.** Two editions of one market are *compared* (ADR-0120 option B, a read
 *   with both values and no difference), never *chosen* between: which edition prices a figure is
 *   a filing decision and needs its own answer first (`set_for_jurisdiction`'s docstring).
 * - **No custom factors** (P-09, phase 2).
 *
 * ## Whose market comes first, and why it is not typed here
 *
 * The sets are listed in the order `GET /factor-sets/market` implies: the set that prices this
 * workspace's sites first, other editions of the same market next, every other market after. The
 * server finds the set with the call the engine prices a site with, so this file holds no table
 * from a jurisdiction to a set and no market or edition literal. A failed market read keeps the
 * library's own order and says so; it is not a failed library.
 *
 * Every row shows its own publisher, edition, publication date, file hash and, where the row
 * records one, the table or cell it was read from (the `notes` text) - the row's own source when
 * it cites one (ADR-0197), else the set's, and which it was. A factor counting CO2 alone says so.
 *
 * ## Three states, and an empty library is a fact rather than a failure
 *
 * - **failed** — `role="alert"`, the failure's own sentence and a retry; no table, and in
 *   particular no *"no factor sets"* sentence. A dropped connection is not a statement about the
 *   deployment.
 * - **loading** — said, not blank.
 * - **loaded** — either sets, or `data-testid="factor-library-empty"`: no set is loaded in this
 *   deployment, so no figure can be priced. That *is* a state a deployment can be in (a database
 *   the loader has not run against) and it is printed as one.
 *
 * ## Rules 3 and 5
 *
 * A value is the row's own string. The only transformation is `factorValue`, which drops the
 * trailing zeros the `NUMERIC(18, 8)` column added — the same trim the receipt uses, so a value
 * reads identically in both places — and the untrimmed string stays in `data-factor-value`. Nothing
 * here is converted to a number.
 *
 * Every sentence about licensing comes from a row: the attribution statement OGL v3.0 requires
 * (U-09) is `attribution_statement`, not a literal in this file.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";
import type { ReactNode } from "react";

import { useFactorMarket } from "../api/factorEditions";
import { useFactorLibrary } from "../api/factors";
import type { FactorLibraryFactor, FactorLibrarySet } from "../api/factors";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { LazyDetails } from "../components/ui/LazyDetails";
import { categoryLabel } from "../lib/categoryLabel";
import { factorValue, shortDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { factorRouteId } from "./factorRoute";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import own from "./FactorLibrary.module.css";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";
import { FactorLibraryOverview } from "./FactorLibraryOverview";
import { FactorPublicationComparison } from "./FactorPublicationComparison";
import { FactorLibrarySpendSets } from "./FactorLibrarySpendSets";
import { FactorEditionComparison } from "./FactorEditionComparison";
import {
  NO_FILTERS,
  answers,
  categoriesIn,
  isCo2Only,
  orderSets,
  provenanceOf,
} from "./factorLibraryView";
import type { Filters, OrderedSet } from "./factorLibraryView";

/** A value the row does not carry, said as a decision rather than left blank (KEEP #10). */
function Absent({ children }: { children: ReactNode }) {
  return (
    <span className={own.none} data-absent>
      {children}
    </span>
  );
}

function Fact({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className={own.fact} data-fact={name}>
      <dt>{LABELS[name] ?? name}</dt>
      <dd>{children}</dd>
    </div>
  );
}

const LABELS: Record<string, string> = {
  publisher: "Publisher",
  edition: "Edition",
  jurisdiction: "Jurisdiction",
  published_at: "Published",
  retrieved_at: "Retrieved",
  source: "Source",
  source_sha256: "File hash (SHA-256)",
  gwp_set_label: "GWP set",
  licence: "Licence",
  locked: "Locked",
};

function Components({ parts }: { parts: Record<string, string> | null }) {
  if (parts === null) return <Absent>none published</Absent>;
  return (
    <>
      {Object.entries(parts).map(([gas, amount], index) => (
        <span key={gas}>
          {index > 0 ? " · " : ""}
          {gas} {factorValue(amount)}
        </span>
      ))}
    </>
  );
}

/** A file hash shortened to what a reader can compare by eye; the whole digest is in the cell's
 * `title` and `data-sha256`, and in full on the factor's own page. */
const HASH_SHOWN = 12;

function FactorRow({ set, factor }: { set: FactorLibrarySet; factor: FactorLibraryFactor }) {
  const origin = provenanceOf(set, factor);
  const co2Only = isCo2Only(factor);
  return (
    <tr
      data-factor-row
      data-category={factor.category}
      data-fuel={factor.fuel_or_carrier}
      data-region={factor.region}
      data-unit-in={factor.unit_in}
      data-co2-only={co2Only ? "yes" : "no"}
    >
      <td>
        {/* The way into the factor's own page (Batch 5). The cell's text is still the category and
            nothing else, so the table's cells read as they did; the row's `data-*` set is unchanged. */}
        <Link
          to="/factors/$factorId"
          params={{ factorId: factorRouteId(set.key, factor) }}
          search={keepSearch}
          className={own.url}
          data-testid="factor-row-link"
          title={`Open ${factor.fuel_or_carrier}, ${factor.category}`}
        >
          {factor.category}
        </Link>
      </td>
      <td>{factor.fuel_or_carrier}</td>
      <td>{factor.region === "" ? <Absent>all</Absent> : factor.region}</td>
      <td>{factor.unit_in}</td>
      <td className={own.num} data-factor-value={factor.value}>
        {factorValue(factor.value)} <span className={own.unit}>{factor.unit_out}</span>
        {co2Only ? (
          <>
            {" "}
            <StatusPill tone="warn" testId="factor-co2-only">
              CO₂ only
            </StatusPill>
          </>
        ) : null}
      </td>
      <td>
        <Components parts={factor.components} />
      </td>
      {/* Provenance, per row (ADR-0197): the row's own publisher and edition, and its source's
          publication date and file hash - the row's own when it cites one, else the set's, and
          which one it was is said. Never a hash without its file, or a file without saying the hash
          is absent. */}
      <td data-col="publisher">{factor.publisher}</td>
      <td data-col="edition">{factor.edition}</td>
      <td data-col="published" data-source-from={origin.from}>
        {origin.publishedAt ? shortDate(origin.publishedAt) : <Absent>not stated</Absent>}
      </td>
      <td
        data-col="source"
        data-source-from={origin.from}
        data-sha256={origin.sha256 ?? ""}
        title={origin.sha256 ?? undefined}
      >
        {origin.url ? (
          <a href={origin.url} target="_blank" rel="noreferrer noopener" className={own.url}>
            source file
          </a>
        ) : (
          <Absent>no source recorded</Absent>
        )}{" "}
        {origin.sha256 ? (
          <span className={own.mono}>
            {origin.sha256.slice(0, HASH_SHOWN)}
            {origin.from === "set" ? " (set)" : ""}
          </span>
        ) : (
          <Absent>no hash</Absent>
        )}
      </td>
      <td className={own.mono}>
        {factor.external_id ?? <Absent>none given</Absent>}
      </td>
      <td className={own.mono}>{factor.verification_row}</td>
      <td data-col="location">{factor.notes ?? <Absent>none recorded</Absent>}</td>
    </tr>
  );
}

/** How a set relates to this workspace, in words (never colour alone). The counted thing is what the
 * server counted: sites, legal entities, or nothing yet (the deployment's configured market). */
function Relation({ entry, basis }: { entry: OrderedSet; basis: string | undefined }) {
  if (entry.relation === "prices_here") {
    const what =
      basis === "sites"
        ? `${entry.count} of this workspace’s sites`
        : basis === "legal_entities"
          ? `this workspace’s ${entry.count} legal entit${entry.count === 1 ? "y" : "ies"}`
          : "the market this deployment is set up for";
    return (
      <StatusPill tone="ok" testId="factor-set-relation">
        Prices {what}
      </StatusPill>
    );
  }
  if (entry.relation === "own_market_history") {
    return (
      <StatusPill tone="neutral" testId="factor-set-relation">
        An earlier edition of this workspace’s market
      </StatusPill>
    );
  }
  return null;
}

function SetPanel({
  entry,
  basis,
  allSets,
  filters,
}: {
  entry: OrderedSet;
  basis: string | undefined;
  allSets: readonly FactorLibrarySet[];
  filters: Filters;
}) {
  const { set } = entry;
  const shown = set.factors.filter((factor) => answers(set, factor, filters, categoryLabel));
  const narrowed =
    filters.query.trim() !== "" || filters.category !== "" || filters.co2OnlyOnly;
  return (
    <Panel title={set.label} sub={`${set.publisher} · ${set.edition}`} column="1 / 13">
      <section
        data-testid="factor-set"
        data-set-key={set.key}
        data-jurisdiction={set.jurisdiction}
        data-locked={set.locked ? "yes" : "no"}
        data-factor-count={set.factors.length}
        data-relation={entry.relation}
        data-prices-new-figures={set.prices_new_figures ? "yes" : "no"}
      >
        <p className={own.relation}>
          <Relation entry={entry} basis={basis} />{" "}
          <StatusPill tone={set.prices_new_figures ? "ok" : "neutral"} testId="factor-set-edition-status">
            {set.prices_new_figures ? "Current edition" : "Kept for history"}
          </StatusPill>
        </p>
        {set.prices_new_figures ? null : (
          <p className={own.attribution} data-testid="factor-set-retired">
            <strong>Kept for history.</strong> A figure priced from this edition keeps citing it. New and
            recalculated figures are priced from its replacement, which records the same publisher values
            with a corrected label and a source for each factor.
          </p>
        )}
        <dl className={own.facts}>
          <Fact name="publisher">{set.publisher}</Fact>
          <Fact name="edition">{set.edition}</Fact>
          <Fact name="jurisdiction">{set.jurisdiction}</Fact>
          <Fact name="published_at">
            {set.published_at ? shortDate(set.published_at) : <Absent>not stated</Absent>}
          </Fact>
          <Fact name="retrieved_at">{shortDate(set.retrieved_at)}</Fact>
          <Fact name="source">
            {set.source_url ? (
              <a href={set.source_url} target="_blank" rel="noreferrer noopener" className={own.url}>
                {set.source_url}
              </a>
            ) : (
              <Absent>no source recorded</Absent>
            )}
          </Fact>
          <Fact name="source_sha256">
            {set.source_sha256 ? (
              <span className={own.mono}>{set.source_sha256}</span>
            ) : (
              <Absent>no file hash recorded</Absent>
            )}
          </Fact>
          <Fact name="gwp_set_label">{set.gwp_set_label}</Fact>
          <Fact name="licence">{set.licence ?? <Absent>none recorded</Absent>}</Fact>
          <Fact name="locked">
            {/* A state, so a pill: the word stays and the icon is added, never colour alone. The
                text is the same string as before, so the gate's exact-text check holds. */}
            {set.locked ? (
              <StatusPill tone="ok">{set.locked_at ? shortDate(set.locked_at) : "yes"}</StatusPill>
            ) : (
              <StatusPill tone="crit">NOT LOCKED</StatusPill>
            )}
          </Fact>
        </dl>

        {set.attribution_statement ? (
          <p className={own.attribution} data-testid="factor-set-attribution">
            {set.attribution_statement}
          </p>
        ) : (
          <p className={own.attribution} data-testid="factor-set-attribution-absent">
            No attribution statement is recorded for this set.
          </p>
        )}

        {/* **Scrolls inside its own box**, with the header held, so two sets of forty rows do not
            push each other's provenance off the screen. A region with a name and a tab stop, as the
            comparison table below it has, so a keyboard reader can scroll it. */}
        <div
          className={`${own.scroll} ${own.tableBox}`}
          tabIndex={0}
          role="region"
          aria-label={`Factors in ${set.label}, scrollable`}
        >
          <table className={own.table} data-testid="factor-table" data-matching={shown.length}>
            <caption className={own.caption}>
              {narrowed
                ? `${shown.length} of ${set.factors.length} factors in ${set.label} match`
                : `${set.factors.length} factor${set.factors.length === 1 ? "" : "s"} in ${set.label}`}
            </caption>
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Fuel or carrier</th>
                <th scope="col">Region</th>
                <th scope="col">Per</th>
                <th scope="col">Value</th>
                <th scope="col">Components</th>
                <th scope="col">Publisher</th>
                <th scope="col">Edition</th>
                <th scope="col">Published</th>
                <th scope="col">Source file and hash</th>
                <th scope="col">Publisher’s id</th>
                <th scope="col">Verified as</th>
                <th scope="col">Table, row or note (as recorded)</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((factor) => (
                <FactorRow
                  key={[factor.category, factor.fuel_or_carrier, factor.region, factor.unit_in].join("|")}
                  set={set}
                  factor={factor}
                />
              ))}
              {shown.length === 0 && narrowed ? (
                <tr>
                  <td colSpan={13} data-testid="factor-no-match">
                    No factor in this set matches that search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
      <FactorEditionComparison sets={allSets} into={set.key} />
      <FactorPublicationComparison set={set} />
    </Panel>
  );
}

function FactorLibraryBody() {
  const screen = useScreen();
  const library = useFactorLibrary();
  const market = useFactorMarket();
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const ordered = library.data ? orderSets(library.data.sets, market.data) : [];
  const categories = library.data ? categoriesIn(library.data.sets) : [];

  return (
    <ScreenFrame screen={screen} title="Factor library" crumb="published sets, locked" bodyScrolls>
      {library.isError ? <FactorLibraryOverview status="failed" /> : library.isPending || !library.data ? (
        <FactorLibraryOverview status="pending" />
      ) : <FactorLibraryOverview status="ready" sets={library.data.sets} />}
      {/* The honesty copy, kept and ranked: collapsed to one line. A `<summary>` is not a button to
          the gate's control census, and nothing in it edits or chooses. */}
      <LazyDetails
        className={own.limits}
        testId="factor-basis-limits"
        summary="Basis and limits: what prices a figure"
      >
        <p className={styles.note}>
          The published factor sets that price every figure: who published each, which edition, and
          when it was retrieved. Read-only. Every figure’s receipt cites a factor listed here. A set is locked when it is loaded, and a
          publisher’s correction becomes a new edition rather than a change to one of these, so this
          screen only reads. Spend-based multiplier sets, used for purchases, are held separately: they
          are listed in their own panel below as provenance only, with no multiplier shown.
        </p>
      </LazyDetails>
      {market.isError ? (
        /* A failed read is shown as one, in a live region, like every other read on every screen
           (`screenStates.test.tsx`), wherever the library itself stands. It does not replace the
           library: the sets are still the library's own and are listed in its order until the
           market can be read. */
        <ReadFailed
          of="The workspace’s market"
          error={market.error}
          onRetry={() => void market.refetch()}
        />
      ) : null}
      {library.error ? (
        <Panel title="Factor sets" column="1 / 13">
          <ReadFailed
            of="The factor library"
            error={library.error}
            onRetry={() => void library.refetch()}
          />
        </Panel>
      ) : library.isPending || library.data === undefined ? (
        <Panel title="Factor sets" column="1 / 13">
          <Empty>Loading…</Empty>
        </Panel>
      ) : library.data.sets.length === 0 ? (
        <Panel title="Factor sets" column="1 / 13">
          <div data-testid="factor-library-empty">
            <p className={styles.note}>
              No factor set is loaded in this deployment, so no figure can be priced here. Loading
              the library is an operator step; a workspace cannot do it.
            </p>
          </div>
        </Panel>
      ) : (
        <>
          {/* Three narrowing controls over every set: a search, a category and a CO2-only switch. They
              filter rows this screen already holds and send nothing, so the library is still read
              once with no parameters - and there is still no edition to pick, only rows to find. */}
          <Panel title="Find a factor" sub="Narrows the rows of every set below" column="1 / 13">
            <div className={own.filters}>
              <label className={own.search}>
                <span>Search the factors</span>
                <input
                  type="search"
                  data-testid="factor-search"
                  aria-label="Search the factors"
                  value={filters.query}
                  placeholder="A fuel, a region, a unit, a publisher's id"
                  onChange={(event) => setFilters({ ...filters, query: event.target.value })}
                />
              </label>
              <label className={own.search}>
                <span>Kind of factor</span>
                <select
                  data-testid="factor-category-filter"
                  value={filters.category}
                  onChange={(event) => setFilters({ ...filters, category: event.target.value })}
                >
                  <option value="">All kinds</option>
                  {categories.map(({ category, count }) => (
                    <option key={category} value={category}>
                      {categoryLabel(category)} ({count})
                    </option>
                  ))}
                </select>
              </label>
              <label className={own.check}>
                <input
                  type="checkbox"
                  data-testid="factor-co2-filter"
                  checked={filters.co2OnlyOnly}
                  onChange={(event) => setFilters({ ...filters, co2OnlyOnly: event.target.checked })}
                />
                Only factors that count CO₂ alone
              </label>
            </div>
            <p className={styles.note} data-testid="factor-order-note">
              {market.data === undefined
                ? market.isError
                  ? "The workspace’s market could not be read, so the sets are listed in the library’s own order."
                  : "Reading the workspace’s market…"
                : market.data.priced_with.length === 0
                  ? "No set prices this workspace’s jurisdictions, so the sets are listed in the library’s own order."
                  : `Listed first: ${
                      market.data.basis === "sites"
                        ? "the set that prices this workspace’s sites"
                        : market.data.basis === "legal_entities"
                          ? "the set that prices this workspace’s legal entities (it has no sites yet)"
                          : "the set for the market this deployment is set up for (this workspace has no sites or legal entities yet)"
                    }.`}
              {market.data !== undefined && market.data.unpriced_jurisdictions.length > 0
                ? ` No set prices ${market.data.unpriced_jurisdictions.join(", ")}.`
                : ""}
            </p>
          </Panel>
          {ordered.map((entry) => (
            <SetPanel
              key={entry.set.key}
              entry={entry}
              basis={market.data?.basis}
              allSets={library.data.sets}
              filters={filters}
            />
          ))}
        </>
      )}
      <FactorLibrarySpendSets />
    </ScreenFrame>
  );
}

export function FactorLibraryScreen() {
  return (
    <RequireSession>
      <FactorLibraryBody />
    </RequireSession>
  );
}
